import { Button, Checkbox, Select, Space, Spin, Tooltip, message } from 'antd';
import { cloneDeep, isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Icon from '@ant-design/icons';
import { partitionOptions } from '@components/dimensions/utils/partition';
import InfoIcon from '@icons/emp/info.svg';
import SankeyNoDataIcon from '@icons/emp/sankey_no_data.svg';
import {
  useGetSankeyDimensions,
  useGetUserIdBySinglePath,
  useShowSankey,
} from '@src/emp-3.0/api/queryHooks';
import Configurations, { eventJourneyOptions } from '@src/emp-3.0/components/Sankey/Configurations';
import GlobalFilter from '@src/emp-3.0/components/Sankey/GlobalFilter';
import PathConversion from '@src/emp-3.0/components/Sankey/PathConversion';
import SankeyChart from '@src/emp-3.0/components/Sankey/SankeyChart';
import UsersTable from '@src/emp-3.0/components/Sankey/UsersTable';
import {
  DragButton,
  FoldButton,
  FoldingStrip,
} from '@src/emp-3.0/components/Sankey/components/FoldPanel';
import { useSankeyStore } from '@src/emp-3.0/components/Sankey/store';
import DpiDeprecationAlert from '@src/emp-3.0/components/common/DpiDeprecationAlert';
import {
  APP_ID,
  formatFilters,
  formatInterval,
  removeSankeyEmptyFilters,
  isInactiveInterval,
  verifySankeyPayload,
} from '@src/emp-3.0/components/Sankey/utils';
import {
  ESystemType,
  IEventFilter,
  ILandingEvent,
  ISankeyRequestBody,
  TDirection,
} from '@src/emp-3.0/types';
import {
  trackGenerateSankey,
  trackGenerateSankeyFailure,
  trackGenerateSankeySuccess,
  trackViewUserTimelineFromSankey,
} from '@src/sensor';

import { defaultCalenderBoundry, stepOption, topNodesOption } from './constants';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import styles from './index.less';
const EMPTY_ARRAY = [];
const LEFT_PANEL_INITIAL_WIDTH = 450;
import { OutOfRetentionMessage } from '@src/emp-3.0/components/Funnel';
import { useGoToUserTimeline } from '@src/hooks/useGoToUserTimeline';
import usePageTitle from '@src/hooks/usePageTitle';
// import { usePageInfoStore } from '@pulse/nav';
const VerticalSpacer = () => {
  return (
    <div style={{ width: 16, height: 28, display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 1, height: '100%', background: '#D9D9D9' }} />
    </div>
  );
};

const SankeyPage = ({ globalFilter, interval, eventName, onModalClose, onConvertToFunnel }) => {
  // usePageInfoStore.setState({
  //   productName: 'ECO',
  //   logoHref: '/app/app-insights'
  // });
  const {
    sankeyRequestBody,
    setSankeyRequestBody,
    showConfigModal,
    setShowConfigModal,
    sankeyData,
    setSankeyData,
    selectedNodeIds,
    setSelectedNodeIds,
    availableNodes,
    setAvailableNodes,
    selectedNodeInstances,
    setSelectedNodeInstances,
    generateSankeyRequestBody,
    setGenerateSankeyRequestBody,
  } = useSankeyStore((state) => state);
  usePageTitle({ text: 'Paths' });
  const onGoToUserTimeline = useGoToUserTimeline();

  const { userInfo, trendsCalenderBoundry: calenderBoundry } = useInitialInfoStore();
  const dataAccessMonths = userInfo.data_access_months || 13;

  const [userIds, setUserIds] = useState<{ app_user_id: string }[]>([]);
  const [sankeyLoading, setSankeyLoading] = useState(false);
  const [foldConfigurationsPanel, setFoldConfigurationsPanel] = useState<boolean>(false);
  const [leftWidth, setLeftWidth] = useState<number>(LEFT_PANEL_INITIAL_WIDTH);

  const { mutate: getSankey, isLoading: isGetSankeyLoading } = useShowSankey();
  const { mutate: getUserId, isLoading: isGetUserIdLoading } = useGetUserIdBySinglePath();
  const { data: sankeyDimensionList = EMPTY_ARRAY } = useGetSankeyDimensions();

  const selectedNodes = useMemo(
    () => availableNodes.filter((node) => selectedNodeIds.includes(node.id)),
    [availableNodes, selectedNodeIds],
  );

  const foldPrompt = useMemo(
    () => `${foldConfigurationsPanel ? 'Show' : 'Hide'} Configuration Panel`,
    [foldConfigurationsPanel],
  );

  const user_id_key = 'client_id';

  const { level, collapse_repeated_events, top_k_nodes } = sankeyRequestBody;
  const { landing_events, direction } = generateSankeyRequestBody;
  const [directionForResult, setDirectionForResult] = useState<TDirection>(direction);
  const [landingEventsForResult, setLandingEventsForResult] =
    useState<ILandingEvent[]>(landing_events);

  const hasSankeyData = sankeyData?.nodes?.length && sankeyData?.links?.length;

  const removeEmptyFilters = useCallback(
    (payloadPartial: Partial<ISankeyRequestBody> = {}) => {
      return removeSankeyEmptyFilters({
        ...sankeyRequestBody,
        ...payloadPartial,
      }) as ISankeyRequestBody;
    },
    [sankeyRequestBody],
  );

  const fetchSankeyData = (payloadPartial?) => {
    const { validation } = verifySankeyPayload({ ...sankeyRequestBody, ...payloadPartial });
    if (!validation) {
      return;
    }
    const payload = removeEmptyFilters(payloadPartial);
    if (!payload) {
      return;
    }
    setGenerateSankeyRequestBody(
      cloneDeep({
        ...payload,
        selected_node_ids: selectedNodeIds,
      }),
    );
    setAvailableNodes([]);
    setSelectedNodeIds([]);
    setSankeyLoading(true);
    trackGenerateSankey({ ...payload });
    getSankey(
      { payload },
      {
        onSuccess: (data) => {
          trackGenerateSankeySuccess({ ...payload });

          if (data?.data?.links && data?.data?.nodes) {
            setSankeyData(data?.data);
          } else {
            setSankeyData({
              direction: 'forward',
              links: [],
              nodes: [],
            });
          }
          if ((data?.data?.links ?? []).length === 0 && (data?.data?.nodes ?? []).length === 0) {
            setAvailableNodes([]);
            setSelectedNodeIds([]);
          }
          setShowConfigModal(false);
          setSankeyLoading(false);
        },
        onError: (error: any) => {
          trackGenerateSankeyFailure({ ...payload });
          message.error(error?.response?.data?.msg).then();
          setSankeyLoading(false);
        },
      },
    );
  };
  const outOfRetention = useMemo(() => {
    const start = generateSankeyRequestBody?.interval?.split('/')[0];
    const limitStart = moment().startOf('day').subtract(dataAccessMonths, 'months');
    if (moment(start).isBefore(limitStart)) {
      return true;
    }
    return false;
  }, [generateSankeyRequestBody, dataAccessMonths]);

  const fetchClientId = useCallback(() => {
    let newPath: { [step: string]: string } | null = null;
    sankeyData?.nodes?.forEach((item) => {
      if (selectedNodeIds.includes(item.id)) {
        newPath = { ...newPath, [`step_${item.level + 1}`]: item.display_name };
      }
    });
    const payload = removeEmptyFilters(generateSankeyRequestBody);
    if (hasSankeyData && selectedNodeIds.length) {
      getUserId(
        {
          payload: {
            ...payload,
            path: newPath,
            target_field: user_id_key,
            limit: 500,
          },
        },
        {
          onSuccess: (data) => {
            const filterUnknownData = (data?.data ?? []).filter(
              (item) => item[user_id_key] !== 'UNKNOWN',
            );
            setUserIds(filterUnknownData);
          },
          onError: (error: any) => {
            message.error(error?.response?.data?.msg).then();
          },
        },
      );
    } else {
      setUserIds([]);
    }
  }, [hasSankeyData, sankeyData?.nodes, generateSankeyRequestBody, selectedNodeIds]);

  useEffect(() => {
    setLandingEventsForResult(landing_events);
  }, [landing_events]);

  useEffect(() => {
    setDirectionForResult(direction);
  }, [direction]);

  useEffect(() => {
    if (selectedNodeIds.length) {
      fetchClientId();
    } else {
      setUserIds([]);
    }
  }, [selectedNodeIds, fetchClientId]);

  const initSankeyRequestBody = useMemo(() => {
    let usefulFilters: IEventFilter[][] = [[{ field: APP_ID, op: 'eq', value: [] }]];
    if (globalFilter?.length > 0) {
      usefulFilters = formatFilters(globalFilter, sankeyDimensionList);
    }

    const intervalFormat = formatInterval(interval, calenderBoundry);
    return cloneDeep({
      filters: usefulFilters,
      interval: intervalFormat,
      landing_events: [{ event_name: eventName, filters: [] }],
    });
  }, [globalFilter, interval, eventName, sankeyDimensionList, calenderBoundry]);
  useEffect(() => {
    if (sankeyLoading || showConfigModal || !eventName) {
      return;
    }

    if (eventName !== generateSankeyRequestBody?.landing_events[0]?.event_name) {
      setShowConfigModal(true);
      return;
    }

    return () => {
      hasSankeyData && setShowConfigModal(false);
    };
  }, [
    eventName,
    hasSankeyData,
    sankeyLoading,
    showConfigModal,
    setShowConfigModal,
    generateSankeyRequestBody?.landing_events,
  ]);

  // Prevents the browser window from being dragged from the large screen to the small screen
  // when the drag button is attached to the right edge, causing the drag button to be invisible.
  useEffect(() => {
    const handleResize = () => {
      const maxWidth = window.innerWidth - 30;
      if (leftWidth > maxWidth) {
        setLeftWidth(maxWidth);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [leftWidth]);

  const { defaultOptions: globalFilterDimensionOptions } = partitionOptions(
    sankeyDimensionList.filter((item) => item.can_filter === 1),
  );

  const handleChange = (values) => {
    const { filters, interval } = values;
    const payloadPartial = {
      ...(filters && { filters }),
      ...((interval || interval === '') && { interval }),
    };
    setSankeyRequestBody(payloadPartial);
    fetchSankeyData(payloadPartial);
  };

  const handleLevelChange = (level) => {
    setSankeyRequestBody({ level: level });
    fetchSankeyData({ level: level });
  };

  const isSameSet = (set1, set2) => {
    return isEqual(Array.from(set1), Array.from(set2));
  };

  const onCollapseRepeatedEventsCheck = (e) => {
    const checked = e.target.checked ? 1 : 0;
    setSankeyRequestBody({ collapse_repeated_events: checked });
    fetchSankeyData({ collapse_repeated_events: checked });
  };

  const handleFold = () => {
    setFoldConfigurationsPanel(true);
    setLeftWidth(0);
  };
  const handleExtend = () => {
    setFoldConfigurationsPanel(false);
    setLeftWidth(LEFT_PANEL_INITIAL_WIDTH);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    const maxWidth = window.innerWidth - 30;
    const clientX = e.clientX;
    const newLeftWidth = clientX > maxWidth ? maxWidth : clientX;
    if (isNaN(newLeftWidth)) {
      return;
    }
    if (newLeftWidth > 0) {
      setFoldConfigurationsPanel(false);
    } else {
      setFoldConfigurationsPanel(true);
    }
    setLeftWidth(newLeftWidth);
  };

  const mask = useMemo(() => {
    return isInactiveInterval(generateSankeyRequestBody?.interval, calenderBoundry);
  }, [generateSankeyRequestBody?.interval, calenderBoundry]);
  const disableStyle = mask ? { opacity: 0.6, pointerEvents: 'none' } : {};

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        // minHeight: On large displays, prevent gaps between dashboard tab and sankey when sankey does not have data in prod or sjc environments.
        minHeight: 'calc(100vh - 85px)',
      }}
    >
      <DpiDeprecationAlert feature="Journeys" />
      <div style={{ background: '#fff', width: '100%', marginBottom: 8 }}>
        <GlobalFilter
          dimensionOptions={globalFilterDimensionOptions}
          onChange={handleChange}
          initSankeyRequestBody={initSankeyRequestBody}
          mask={mask}
          calenderBoundry={calenderBoundry}
        />
      </div>
      <div
        style={{
          background: '#fff',
          display: 'flex',
          flex: 1,
          width: '100%',
        }}
      >
        <Configurations
          onFetch={fetchSankeyData}
          onModalClose={onModalClose}
          sankeyLoading={sankeyLoading}
          initSankeyRequestBody={initSankeyRequestBody}
          style={{ width: `${leftWidth}px`, ...disableStyle }}
          eventNameFromUrl={eventName}
          calenderBoundry={calenderBoundry}
        />
        <div style={{ display: 'flex', width: '100%', flex: 1, minWidth: 0 }}>
          {foldConfigurationsPanel && (
            <Tooltip title={foldPrompt} placement="right">
              <FoldingStrip onClick={handleExtend} />
            </Tooltip>
          )}
          <div style={{ flex: 1, overflow: 'scroll', position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
                padding: '16px 24px 16px 0',
                marginBottom: '24px',
                overflow: 'scroll',
              }}
            >
              <div style={{ position: 'sticky', left: 0, zIndex: 69, background: '#fff' }}>
                <Tooltip title={foldPrompt}>
                  <FoldButton
                    fold={foldConfigurationsPanel}
                    onClick={() => (foldConfigurationsPanel ? handleExtend() : handleFold())}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <span style={{ flex: 1, fontWeight: 500 }}>
                  The Paths{' '}
                  {eventJourneyOptions.find((item) => item.value === directionForResult)?.label} “
                  {landingEventsForResult[0]?.event_name}”
                </span>
                <div
                  style={{ display: 'flex', flexShrink: 0, alignItems: 'center', ...disableStyle }}
                >
                  <Checkbox
                    checked={!!collapse_repeated_events}
                    onChange={onCollapseRepeatedEventsCheck}
                  >
                    Collapse Repeated Events{' '}
                  </Checkbox>
                  <VerticalSpacer />
                  <div>
                    <span>Step: </span>
                    <Space.Compact>
                      <Button
                        style={{ background: '#F5F5F5' }}
                        onClick={() => {
                          if (level > 3) {
                            handleLevelChange(level - 1);
                          }
                        }}
                      >
                        -
                      </Button>
                      <Select
                        value={level}
                        options={stepOption}
                        onChange={(value) => handleLevelChange(value)}
                      />
                      <Button
                        style={{ background: '#F5F5F5' }}
                        onClick={() => {
                          if (level < stepOption.length + 2) {
                            handleLevelChange(level + 1);
                          }
                        }}
                      >
                        +
                      </Button>
                    </Space.Compact>
                  </div>
                  <VerticalSpacer />
                  <div>
                    <span>Show Top</span>
                    <Select
                      bordered={false}
                      value={top_k_nodes}
                      options={topNodesOption}
                      onChange={(value) => {
                        setSankeyRequestBody({ top_k_nodes: value });
                        fetchSankeyData({ top_k_nodes: value });
                      }}
                      style={{ background: '#F5F5F5', margin: '0 8px' }}
                    />
                    <span>Nodes</span>
                    <Tooltip
                      title={`Buckets below Top ${top_k_nodes} are merged into Other`}
                      placement="topRight"
                    >
                      <Icon style={{ margin: '0 8px' }} component={InfoIcon} />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'sticky', left: 0, zIndex: 69, background: '#fff' }}>
                <DragButton
                  onMouseDown={(e) => {
                    document.addEventListener('mousemove', handleDrag);
                    document.addEventListener(
                      'mouseup',
                      () => {
                        document.removeEventListener('mousemove', handleDrag);
                      },
                      { once: true },
                    );
                  }}
                />
              </div>
              <Spin spinning={isGetSankeyLoading} wrapperClassName={styles.empSankeySpin}>
                {hasSankeyData && !outOfRetention ? (
                  <SankeyChart
                    data={sankeyData}
                    direction={sankeyData.direction}
                    onSelectedNodeChange={(
                      selectedNodeIdsFromChart,
                      availableNodesFromChart,
                      selectedNodeInstances,
                    ) => {
                      if (!isSameSet(new Set(selectedNodeIdsFromChart), new Set(selectedNodeIds))) {
                        setSelectedNodeIds(selectedNodeIdsFromChart);
                      }
                      setAvailableNodes(availableNodesFromChart);
                      setSelectedNodeInstances(selectedNodeInstances);
                    }}
                    selectedNodeIds={selectedNodeIds}
                    onFetch={fetchSankeyData}
                    mask={mask}
                  />
                ) : (
                  <div className={styles.noData}>
                    <Icon
                      style={{ fontSize: 104, marginBottom: 12 }}
                      component={SankeyNoDataIcon}
                    />
                    <div>
                      {outOfRetention
                        ? OutOfRetentionMessage.replace('1', dataAccessMonths + '')
                        : 'No data available to create a Sankey diagram.'}
                    </div>
                    <div>Please adjust the configuration and try again.</div>
                  </div>
                )}
              </Spin>
            </div>
            <div style={{ padding: '0 24px', width: '100%' }}>
              <PathConversion
                interval={generateSankeyRequestBody?.interval}
                filters={generateSankeyRequestBody?.filters}
                selectedNodes={selectedNodes}
                sankeyData={sankeyData}
                selectedNodeInstances={selectedNodeInstances}
                onClearPath={() => {
                  setSelectedNodeIds([]);
                }}
                onConvertToFunnel={onConvertToFunnel}
                mask={mask}
              />
              <Spin spinning={isGetUserIdLoading}>
                <UsersTable
                  userKey={user_id_key}
                  dataSource={userIds}
                  handleEvent={(event, obj) => {
                    trackViewUserTimelineFromSankey({ ...obj });
                    onGoToUserTimeline?.(event, obj);
                  }}
                  interval={generateSankeyRequestBody.interval}
                  disableStyle={disableStyle}
                  filters={generateSankeyRequestBody?.filters}
                  selectedNodeNames={selectedNodeInstances?.map((item) => item.display_name || '')}
                />
              </Spin>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SankeyPage;
